import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell
} from "recharts";
import {
    Box, Typography, CircularProgress, IconButton, Dialog, DialogContent, DialogTitle, 
    useTheme, useMediaQuery, Paper, Stack
} from "@mui/material";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import CloseIcon from "@mui/icons-material/Close";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import { getTopProducts } from "../../services/catalogServices";
import CategorySelector from "../Shared/CategorySelector";
import VendorSelector from "../Shared/VendorSelector";

const CustomTooltip = ({ active, payload }) => {
    if (active && payload?.length) {
        const item = payload[0].payload;
        return (
            <Paper
                elevation={4}
                sx={{
                    p: 2,
                    backgroundColor: "rgba(255, 255, 255, 0.95)",
                    backdropFilter: "blur(4px)",
                    borderRadius: "12px",
                    border: "1px solid #e2e8f0",
                    maxWidth: 300
                }}
            >
                <Typography variant="caption" display="block" sx={{ color: "#64748b", fontWeight: "bold", mb: 1, textTransform: "uppercase" }}>
                    Dettaglio Prodotto
                </Typography>
                <Typography variant="body2" sx={{ color: "#1e293b", fontWeight: 700, mb: 1 }}>
                    {item.productLabel}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, pt: 1, borderTop: "1px solid #f1f5f9" }}>
                    <TouchAppIcon sx={{ fontSize: 16, color: "#0b3d87" }} />
                    <Typography variant="body2" sx={{ color: "#475569", fontWeight: 600 }}>
                        Click Ricevuti:
                    </Typography>
                    <Typography variant="body2" sx={{ ml: "auto", fontWeight: 800, color: "#0b3d87" }}>
                        {item.clickCount}
                    </Typography>
                </Box>
            </Paper>
        );
    }
    return null;
};

CustomTooltip.propTypes = {
    active: PropTypes.bool,
    payload: PropTypes.arrayOf(
        PropTypes.shape({
            payload: PropTypes.shape({
                productLabel: PropTypes.string,
                clickCount: PropTypes.number
            })
        })
    )
};

const GraficoClick = ({ filtroTempoGlobal, startDateGlobal, endDateGlobal }) => {
    const [categoria, setCategoria] = useState(null);
    const [vendor, setVendor] = useState(null);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [openFullscreen, setOpenFullscreen] = useState(false);

    const theme = useTheme();
    const fullScreenModal = useMediaQuery(theme.breakpoints.down('md'));
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const fetchData = useCallback(async () => {
        if (filtroTempoGlobal === "custom" && (!startDateGlobal || !endDateGlobal)) return;

        setLoading(true);
        setError(null);
        try {
            const categoryId = categoria ? categoria.id : null;
            const vendorId = vendor ? vendor.id : null;
            const response = await getTopProducts(filtroTempoGlobal, categoryId, vendorId, 10, startDateGlobal, endDateGlobal);
            if (response) {
                setData(response);
            } else {
                setData([]);
            }
        } catch (err) {
            setError(err.message || "Non è stato possibile recuperare i dati.");
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [filtroTempoGlobal, categoria, vendor, startDateGlobal, endDateGlobal]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const renderChart = (isFullscreen = false) => {
        const chartData = data.map(item => {
            let label = item.productLabel || "";
            if (!isFullscreen && label.length > 25) {
                label = label.substring(0, 22) + "...";
            }
            return {
                ...item,
                displayName: label
            };
        });

        let chartHeight = isFullscreen ? 600 : "100%";

        return (
            <ResponsiveContainer width="100%" height={chartHeight}>
                <BarChart
                    layout="vertical"
                    data={chartData}
                    margin={{ top: 5, right: 40, left: isFullscreen ? 150 : 20, bottom: 5 }}
                >
                    <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="#f1f5f9" />
                    <XAxis type="number" hide={!isFullscreen} tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                    <YAxis
                        dataKey="displayName"
                        type="category"
                        tick={{ fontSize: isFullscreen ? 13 : 10, fontWeight: 500, fill: "#475569" }}
                        width={isFullscreen ? 200 : 100}
                        hide={!isFullscreen && data.length === 0}
                        axisLine={false}
                        tickLine={false}
                    />
                    <Tooltip content={<CustomTooltip />} cursor={{ fill: '#f8fafc' }} />
                    <Bar
                        dataKey="clickCount"
                        radius={[0, 10, 10, 0]}
                        barSize={isFullscreen ? 32 : 24}
                    >
                        {data.map((entry) => (
                            <Cell key={entry.catalogListId} fill={data.indexOf(entry) === 0 ? "#0b3d87" : "#3b82f6"} fillOpacity={1 - (data.indexOf(entry) * 0.12)} />
                        ))}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        );
    };

    let chartContent;
    if (loading) {
        chartContent = (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", minHeight: "200px" }}>
                <CircularProgress size={40} thickness={4} sx={{ color: "#0b3d87" }} />
            </Box>
        );
    } else if (error) {
        chartContent = (
            <Box sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                minHeight: "200px",
                color: "#94a3b8"
            }}>
                <QueryStatsIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Non è stato possibile recuperare i dati
                </Typography>
            </Box>
        );
    } else if (data.length === 0) {
        chartContent = (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", minHeight: "200px" }}>
                <Typography color="textSecondary" sx={{ fontWeight: 500 }}>Nessun dato disponibile.</Typography>
            </Box>
        );
    } else {
        chartContent = renderChart();
    }

    return (
        <Box sx={{ height: "100%", width: "100%", display: "flex", flexDirection: "column" }}>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    mb: isMobile ? 1 : 2
                }}
            >
                <Stack
                    direction={isMobile ? "column" : "row"}
                    spacing={1.5}
                    alignItems="center"
                    justifyContent="center"
                    sx={{ width: "100%", flexWrap: "wrap" }}
                >
                    <Box sx={{ width: isMobile ? "100%" : "auto" }}>
                        <VendorSelector
                            value={vendor}
                            onVendorChange={setVendor}
                            fullWidth={isMobile}
                        />
                    </Box>
                    <Box sx={{ width: isMobile ? "100%" : "auto" }}>
                        <CategorySelector
                            value={categoria}
                            onCategoryChange={setCategoria}
                            fullWidth={isMobile}
                        />
                    </Box>
                </Stack>
            </Box>

            <Box sx={{ flexGrow: 1, position: "relative", minHeight: isMobile ? "200px" : "250px" }}>
                {chartContent}

                <IconButton
                    size="small"
                    onClick={() => setOpenFullscreen(true)}
                    title="Ingrandisci"
                    sx={{
                        position: "absolute",
                        bottom: 10,
                        right: 10,
                        backgroundColor: "#f1f5f9",
                        borderRadius: "10px",
                        padding: "6px",
                        zIndex: 10,
                        "&:hover": { backgroundColor: "#e2e8f0" }
                    }}
                >
                    <FullscreenIcon fontSize="small" sx={{ color: "#0b3d87" }} />
                </IconButton>
            </Box>

            <Dialog
                fullScreen={fullScreenModal}
                open={openFullscreen}
                onClose={() => setOpenFullscreen(false)}
                maxWidth="lg"
                fullWidth
                slotProps={{
                    paper: { sx: { borderRadius: fullScreenModal ? 0 : "24px", overflow: "hidden" } }
                }}
            >
                <DialogTitle sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    color: "#0b3d87",
                    borderBottom: "1px solid #f1f5f9",
                    px: 3,
                    py: 2
                }}>
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                        Classifica Top 10 Prodotti
                    </Typography>
                    <IconButton onClick={() => setOpenFullscreen(false)}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 4, backgroundColor: "#fdfdfd" }}>
                    <Box sx={{ mt: 2, height: "600px", width: "100%" }}>
                        {renderChart(true)}
                    </Box>
                </DialogContent>
            </Dialog>
        </Box>
    );
};

GraficoClick.propTypes = {
    filtroTempoGlobal: PropTypes.string,
    startDateGlobal: PropTypes.string,
    endDateGlobal: PropTypes.string
};

export default GraficoClick;
 